import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Table, Button, Form, Modal, Pagination, Row, Col, Badge, InputGroup, Spinner } from 'react-bootstrap';
import { 
  FaTruck, FaMapMarkedAlt, FaEye, FaEdit, FaSearch, FaSyncAlt, 
  FaCalendarAlt, FaUser, FaPhoneAlt, FaBoxOpen, FaCheckCircle, 
  FaTimesCircle, FaClock, FaPaperPlane, FaThLarge, FaList, 
  FaAngleDoubleLeft, FaAngleDoubleRight, FaChevronLeft, FaChevronRight,
  FaTimes, FaExternalLinkAlt, FaMapMarkerAlt, FaCommentAlt, FaShieldAlt
} from 'react-icons/fa';
import { get, post } from '../utils/api';
import { useToast } from './ToastContext';
import './DeliveryManagement.css';
import ProgressDialog from './ProgressDialog';
import NoDataCard from './NoDataCard';

const DISPATCH_PRESETS = [
  "Package dispatched with delivery partner",
  "Out for delivery — rider en route to address",
  "Delivered safely to customer / recipient",
  "Customer unavailable / delivery rescheduled",
  "Incorrect delivery address or contact unreachable"
];

const DeliveryManagement = () => {
  const { showToast } = useToast();
  const [deliveryList, setDeliveryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [pagination, setPagination] = useState({ total: 0, current_page: 1, total_pages: 1, limit: 12 });
  
  // Modals & States
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentDelivery, setCurrentDelivery] = useState(null);
  const [deliveryDetails, setDeliveryDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  const [newStatus, setNewStatus] = useState('');
  const [deliveryDateInput, setDeliveryDateInput] = useState('');
  const [remarksInput, setRemarksInput] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'

  const fetchDeliveries = useCallback(async (p = page, l = limit) => {
    setLoading(true);
    try {
      const response = await get(`/delivery?page=${p}&limit=${l}`);
      if (response && response.success) {
        setDeliveryList(response.data || []);
        if (response.pagination) {
          setPagination(response.pagination);
          setPage(response.pagination.current_page || p);
          setLimit(response.pagination.limit || l);
        }
      } else {
        showToast(response?.message || 'Failed to fetch deliveries.', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Failed to fetch deliveries.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [page, limit, showToast]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > (pagination.total_pages || 1)) return;
    fetchDeliveries(newPage, limit);
  };

  // Helpers for datetime-local input formatting
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  const formatForDatetimeLocal = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const year = d.getFullYear();
      const month = pad(d.getMonth() + 1);
      const day = pad(d.getDate());
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      return '';
    }
  };

  const handleShowModal = (delivery) => {
    setCurrentDelivery(delivery);
    setNewStatus(delivery.status || 'pending');
    setDeliveryDateInput(formatForDatetimeLocal(delivery.delivery_date));
    setRemarksInput(delivery.remarks || '');
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentDelivery(null);
    setRemarksInput('');
    setFormErrors({});
  };

  const handleShowDetailsModal = async (delivery) => {
    setCurrentDelivery(delivery);
    setShowDetailsModal(true);
    setDetailsLoading(true);
    try {
      const response = await get(`/delivery/${delivery.delivery_log_id}`);
      if (response && response.success) {
        setDeliveryDetails(response.data);
      } else {
        showToast(response?.message || 'Failed to fetch delivery details.', 'danger');
      }
    } catch (error) {
      showToast(error?.message || 'Failed to fetch delivery details.', 'danger');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setCurrentDelivery(null);
    setDeliveryDetails(null);
  };

  const handleStatusChange = (e) => {
    const val = e.target.value;
    setNewStatus(val);
    if ((val === 'dispatched' || val === 'out_for_delivery' || val === 'delivered') && !deliveryDateInput) {
      setDeliveryDateInput(formatForDatetimeLocal(new Date().toISOString()));
    }
  };

  const handleSubmitStatusUpdate = async (event) => {
    event.preventDefault();
    if (!currentDelivery) return;

    const errors = {};
    if (newStatus === 'cancelled' || newStatus === 'failed') {
      if (!remarksInput || !remarksInput.trim()) {
        errors.remarks = 'Remarks are mandatory when cancelling or marking delivery as failed.';
      }
    }

    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setUpdatingStatus(true);

    try {
      const payload = { status: newStatus };
      if (deliveryDateInput) {
        payload.delivery_date = new Date(deliveryDateInput).toISOString();
      }
      if (remarksInput) payload.remarks = remarksInput.trim();

      const response = await post(`/delivery/${currentDelivery.delivery_log_id}/status`, payload, { method: 'PATCH' });

      if (response && response.success) {
        showToast(response.message || 'Delivery status updated successfully.', 'success');
        handleCloseModal();
        fetchDeliveries();
      } else {
        showToast(response?.message || 'Failed to update delivery status.', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Failed to update delivery status.', 'danger');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'PENDING';
    return status.replace(/_/g, ' ').toUpperCase();
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      case 'delivered':
        return <span className="dlv-status-tag dlv-delivered"><FaCheckCircle className="me-1" /> Delivered</span>;
      case 'out_for_delivery':
        return <span className="dlv-status-tag dlv-out"><FaTruck className="me-1" /> Out for Delivery</span>;
      case 'dispatched':
        return <span className="dlv-status-tag dlv-dispatched"><FaPaperPlane className="me-1" /> Dispatched</span>;
      case 'cancelled':
      case 'failed':
        return <span className="dlv-status-tag dlv-failed"><FaTimesCircle className="me-1" /> {formatStatus(s)}</span>;
      default:
        return <span className="dlv-status-tag dlv-pending"><FaClock className="me-1" /> Pending</span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const d = new Date(dateString);
      if (isNaN(d)) return 'N/A';
      return d.toLocaleString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateString;
    }
  };

  // Metrics KPI calculation
  const metrics = useMemo(() => {
    const total = deliveryList.length;
    const pending = deliveryList.filter(d => (d.status || '').toLowerCase() === 'pending').length;
    const active = deliveryList.filter(d => ['dispatched', 'out_for_delivery'].includes((d.status || '').toLowerCase())).length;
    const delivered = deliveryList.filter(d => (d.status || '').toLowerCase() === 'delivered').length;
    return { total, pending, active, delivered };
  }, [deliveryList]);

  // Filtered & Sorted Deliveries
  const displayedDeliveries = useMemo(() => {
    return deliveryList
      .filter((d) => {
        if (statusFilter !== 'all') {
          const st = (d.status || 'pending').toLowerCase();
          if (statusFilter === 'pending' && st !== 'pending') return false;
          if (statusFilter === 'dispatched' && st !== 'dispatched') return false;
          if (statusFilter === 'out_for_delivery' && st !== 'out_for_delivery') return false;
          if (statusFilter === 'delivered' && st !== 'delivered') return false;
          if (statusFilter === 'cancelled' && (st !== 'cancelled' && st !== 'failed')) return false;
        }

        if (searchTerm.trim() !== '') {
          const q = searchTerm.toLowerCase();
          const matchOrder = String(d.order_id || '').includes(q);
          const matchCourier = (d.delivery_man_name || '').toLowerCase().includes(q);
          const matchCity = (d.city || '').toLowerCase().includes(q);
          const matchAddress = (d.address_line1 || '').toLowerCase().includes(q);
          const matchRemarks = (d.remarks || '').toLowerCase().includes(q);
          if (!matchOrder && !matchCourier && !matchCity && !matchAddress && !matchRemarks) return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'newest') return new Date(b.assigned_date || 0) - new Date(a.assigned_date || 0);
        if (sortBy === 'oldest') return new Date(a.assigned_date || 0) - new Date(b.assigned_date || 0);
        if (sortBy === 'delivery_date') return new Date(b.delivery_date || 0) - new Date(a.delivery_date || 0);
        if (sortBy === 'order_id') return (b.order_id || 0) - (a.order_id || 0);
        return 0;
      });
  }, [deliveryList, statusFilter, searchTerm, sortBy]);

  const hasActiveFilters = Boolean(searchTerm.trim() || statusFilter !== 'all');

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSortBy('newest');
  };

  return (
    <Container fluid className="dlv-mgmt-page px-3 px-lg-4 py-3">
      <ProgressDialog show={loading && deliveryList.length === 0} message="Loading delivery tracking..." />

      {/* Header & KPI Summary Cards */}
      <div className="dlv-header-panel mb-4 p-3 p-lg-4 rounded glassmorphic-panel">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
          <div className="d-flex align-items-center gap-3">
            <div className="dlv-master-icon">
              <FaTruck />
            </div>
            <div>
              <h3 className="dlv-title mb-0">Delivery Management</h3>
              <p className="dlv-subtitle mb-0 text-muted">Track outbound parcel dispatches, assign couriers & manage destination delivery logs.</p>
            </div>
          </div>

          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => fetchDeliveries()} 
            className="dlv-refresh-btn d-flex align-items-center gap-2"
          >
            <FaSyncAlt className={loading ? 'fa-spin' : ''} /> Refresh Deliveries
          </Button>
        </div>

        {/* KPI Metrics Cards */}
        <Row className="g-3">
          <Col xs={6} md={3}>
            <div className="dlv-kpi-card">
              <div className="kpi-icon total">
                <FaBoxOpen />
              </div>
              <div className="kpi-content">
                <div className="kpi-value">{metrics.total}</div>
                <div className="kpi-label">Total Assigned</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="dlv-kpi-card">
              <div className="kpi-icon pending">
                <FaClock />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-warning">{metrics.pending}</div>
                <div className="kpi-label">Pending Dispatch</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="dlv-kpi-card">
              <div className="kpi-icon active">
                <FaTruck />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-info">{metrics.active}</div>
                <div className="kpi-label">Out for Delivery</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="dlv-kpi-card">
              <div className="kpi-icon delivered">
                <FaCheckCircle />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-success">{metrics.delivered}</div>
                <div className="kpi-label">Delivered</div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Filter & Search Toolbar */}
      <div className="dlv-toolbar-panel mb-4 p-3 rounded glassmorphic-panel">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          {/* Search Box */}
          <div className="dlv-search-wrapper flex-grow-1">
            <InputGroup className="dlv-search-group">
              <InputGroup.Text className="bg-transparent border-0 pe-1 text-muted">
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search Order #, Delivery Person, City, Address, Remarks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="dlv-search-input border-0 ps-1"
              />
              {searchTerm && (
                <Button 
                  variant="link" 
                  onClick={() => setSearchTerm('')} 
                  className="dlv-clear-search text-muted border-0"
                >
                  <FaTimes />
                </Button>
              )}
            </InputGroup>
          </div>

          {/* Status Filter Chips */}
          <div className="dlv-status-chips d-flex align-items-center gap-2 flex-wrap">
            <button 
              type="button"
              className={`dlv-filter-chip ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All ({metrics.total})
            </button>
            <button 
              type="button"
              className={`dlv-filter-chip pending ${statusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setStatusFilter('pending')}
            >
              Pending ({metrics.pending})
            </button>
            <button 
              type="button"
              className={`dlv-filter-chip active-chip ${statusFilter === 'out_for_delivery' || statusFilter === 'dispatched' ? 'active' : ''}`}
              onClick={() => setStatusFilter('out_for_delivery')}
            >
              In Transit ({metrics.active})
            </button>
            <button 
              type="button"
              className={`dlv-filter-chip delivered ${statusFilter === 'delivered' ? 'active' : ''}`}
              onClick={() => setStatusFilter('delivered')}
            >
              Delivered ({metrics.delivered})
            </button>
          </div>

          {/* Controls Cluster */}
          <div className="d-flex align-items-center gap-2">
            <Form.Select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="dlv-select-pill"
              size="sm"
            >
              <option value="newest">📅 Newest Assigned</option>
              <option value="oldest">📅 Oldest Assigned</option>
              <option value="delivery_date">🚚 Delivery Date</option>
              <option value="order_id">📦 Order #</option>
            </Form.Select>

            <div className="dlv-view-switch">
              <button 
                type="button" 
                className={`switch-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Card Grid View"
              >
                <FaThLarge />
              </button>
              <button 
                type="button" 
                className={`switch-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                title="Table List View"
              >
                <FaList />
              </button>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="dlv-reset-btn"
                title="Reset all filters"
              >
                <FaTimes className="me-1" /> Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Deliveries List Area */}
      {displayedDeliveries.length === 0 && !loading ? (
        <div className="dlv-no-data-box py-5 text-center rounded glassmorphic-panel">
          <NoDataCard 
            title="No Deliveries Found"
            message={hasActiveFilters ? "No delivery logs match your search or filter criteria." : "There are no deliveries scheduled in the system."}
            icon="🚚"
            actionButton={
              hasActiveFilters ? (
                <Button variant="primary" size="sm" onClick={handleResetFilters}>
                  Clear Filters
                </Button>
              ) : (
                <Button variant="primary" size="sm" onClick={() => fetchDeliveries()}>
                  Refresh Queue
                </Button>
              )
            }
          />
        </div>
      ) : viewMode === 'grid' ? (
        /* Visual Delivery Cards Grid */
        <div className="dlv-cards-grid">
          {displayedDeliveries.map((delivery, idx) => {
            const fullAddress = [
              delivery.address_line1,
              delivery.address_line2,
              delivery.city,
              delivery.state,
              delivery.postal_code
            ].filter(Boolean).join(', ');

            return (
              <div className="dlv-card-item glassmorphic-panel" key={delivery.delivery_log_id || idx}>
                {/* Header */}
                <div className="dlv-card-header p-3 border-bottom d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <div className="dlv-card-icon">
                      <FaTruck />
                    </div>
                    <div>
                      <div className="dlv-order-ref">Order #{delivery.order_id}</div>
                      <span className="dlv-log-id">Log #{delivery.delivery_log_id}</span>
                    </div>
                  </div>
                  {getStatusBadge(delivery.status)}
                </div>

                {/* Body */}
                <div className="dlv-card-body p-3">
                  {/* Courier info */}
                  <div className="dlv-info-row mb-2">
                    <span className="dlv-label">Assigned Courier:</span>
                    <span className="dlv-courier-name">
                      <FaUser className="me-1 small text-info" /> {delivery.delivery_man_name || 'Unassigned'}
                    </span>
                  </div>

                  {/* Destination Address */}
                  <div className="dlv-info-row mb-2">
                    <span className="dlv-label">Destination:</span>
                    <div className="dlv-address-text text-truncate" title={fullAddress || 'N/A'}>
                      <FaMapMarkerAlt className="me-1 small text-danger" /> {fullAddress || 'Address on file'}
                    </div>
                  </div>

                  {/* Assigned & Target Delivery Dates */}
                  <div className="dlv-dates-box p-2 rounded mb-2">
                    <div className="d-flex justify-content-between small mb-1">
                      <span className="text-muted">Assigned:</span>
                      <span className="fw-semibold text-white">{formatDate(delivery.assigned_date)}</span>
                    </div>
                    <div className="d-flex justify-content-between small">
                      <span className="text-muted">Delivered/Target:</span>
                      <span className="fw-semibold text-primary">{formatDate(delivery.delivery_date)}</span>
                    </div>
                  </div>

                  {/* Remarks Note */}
                  {delivery.remarks && (
                    <div className="dlv-remarks-callout p-2 rounded mb-2 small">
                      <FaCommentAlt className="me-1 text-warning" /> <span className="dlv-remarks-text">{delivery.remarks}</span>
                    </div>
                  )}

                  {/* Order Items Snippet */}
                  {Array.isArray(delivery.order_items) && delivery.order_items.length > 0 && (
                    <div className="dlv-items-preview small text-muted pt-2 border-top">
                      📦 <strong>{delivery.order_items.length} item(s):</strong> {delivery.order_items.map(it => it.medicine_name).slice(0, 2).join(', ')}
                      {delivery.order_items.length > 2 && ` +${delivery.order_items.length - 2} more`}
                    </div>
                  )}
                </div>

                {/* Actions Footer */}
                <div className="dlv-card-footer p-2 border-top d-flex gap-2">
                  <Button 
                    variant="outline-info" 
                    size="sm" 
                    className="flex-grow-1 dlv-btn-action"
                    onClick={() => handleShowDetailsModal(delivery)}
                  >
                    <FaEye className="me-1" /> View Details
                  </Button>
                  <Button 
                    variant="primary" 
                    size="sm" 
                    className="flex-grow-1 dlv-btn-action"
                    onClick={() => handleShowModal(delivery)}
                  >
                    <FaEdit className="me-1" /> Update Status
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* High Density Table View */
        <div className="dlv-table-wrapper rounded glassmorphic-panel overflow-hidden">
          <Table responsive hover className="dlv-data-table mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>#</th>
                <th>Order Ref</th>
                <th>Assigned Courier</th>
                <th>Delivery Address</th>
                <th>Assigned Date</th>
                <th>Target / Delivered Date</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedDeliveries.map((delivery, idx) => {
                const fullAddress = [
                  delivery.address_line1,
                  delivery.city,
                  delivery.state,
                  delivery.postal_code
                ].filter(Boolean).join(', ');

                return (
                  <tr key={delivery.delivery_log_id || idx}>
                    <td className="text-muted fw-bold small">#{idx + 1}</td>
                    <td>
                      <span className="dlv-order-tag">Order #{delivery.order_id}</span>
                      <div className="small text-muted">Log #{delivery.delivery_log_id}</div>
                    </td>
                    <td>
                      <div className="fw-semibold text-white">
                        <FaUser className="me-1 text-info small" /> {delivery.delivery_man_name || 'Unassigned'}
                      </div>
                    </td>
                    <td>
                      <div className="dlv-table-address text-truncate" title={fullAddress || 'N/A'} style={{ maxWidth: '220px' }}>
                        <FaMapMarkerAlt className="me-1 text-danger small" /> {fullAddress || 'N/A'}
                      </div>
                    </td>
                    <td className="small text-muted">{formatDate(delivery.assigned_date)}</td>
                    <td className="small text-primary fw-semibold">{formatDate(delivery.delivery_date)}</td>
                    <td>
                      {getStatusBadge(delivery.status)}
                    </td>
                    <td className="text-end">
                      <div className="d-flex align-items-center justify-content-end gap-1">
                        <Button 
                          variant="outline-info" 
                          size="sm" 
                          className="dlv-tbl-btn"
                          onClick={() => handleShowDetailsModal(delivery)}
                          title="View Full Details"
                        >
                          <FaEye />
                        </Button>
                        <Button 
                          variant="primary" 
                          size="sm" 
                          className="dlv-tbl-btn"
                          onClick={() => handleShowModal(delivery)}
                          title="Update Status"
                        >
                          <FaEdit />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {/* Pagination Bar */}
      {pagination.total_pages > 1 && (
        <div className="dlv-pagination-row d-flex justify-content-between align-items-center flex-wrap gap-2 p-2 px-3 rounded mt-3 glassmorphic-panel">
          <div className="dlv-pagination-info text-muted small">
            Page <span className="fw-bold text-white">{pagination.current_page}</span> of <span className="fw-bold text-white">{pagination.total_pages}</span> ({pagination.total} deliveries)
          </div>

          <Pagination className="dlv-pagination-nav mb-0">
            <Pagination.First onClick={() => handlePageChange(1)} disabled={page <= 1}><FaAngleDoubleLeft /></Pagination.First>
            <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page <= 1}><FaChevronLeft /></Pagination.Prev>
            
            {Array.from({ length: Math.min(pagination.total_pages, 7) }).map((_, idx) => {
              const pageNum = idx + 1;
              return (
                <Pagination.Item key={pageNum} active={pageNum === page} onClick={() => handlePageChange(pageNum)}>
                  {pageNum}
                </Pagination.Item>
              );
            })}

            <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page >= pagination.total_pages}><FaChevronRight /></Pagination.Next>
            <Pagination.Last onClick={() => handlePageChange(pagination.total_pages)} disabled={page >= pagination.total_pages}><FaAngleDoubleRight /></Pagination.Last>
          </Pagination>
        </div>
      )}

      {/* Update Delivery Status Modal */}
      <Modal show={showModal} onHide={handleCloseModal} scrollable className="dlv-modal-modern">
        <Modal.Header closeButton className="dlv-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="dlv-modal-icon">
              <FaTruck />
            </div>
            <div>
              <Modal.Title className="dlv-modal-title mb-0">Update Delivery Status</Modal.Title>
              <div className="dlv-modal-subtitle">
                Order <span className="fw-bold text-primary">#{currentDelivery?.order_id}</span> • Courier <span className="fw-bold text-info">{currentDelivery?.delivery_man_name || 'N/A'}</span>
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="dlv-modal-body p-4">
          {currentDelivery && (
            <Form onSubmit={handleSubmitStatusUpdate}>
              <Form.Group className="mb-3">
                <Form.Label className="dlv-modal-label">Delivery Status</Form.Label>
                <Form.Select 
                  value={newStatus} 
                  onChange={handleStatusChange}
                  className="dlv-modal-select"
                >
                  <option value="pending">⏳ Pending Assignment</option>
                  <option value="dispatched">📦 Dispatched (With Courier)</option>
                  <option value="out_for_delivery">🚚 Out for Delivery</option>
                  <option value="delivered">✅ Delivered to Customer</option>
                  <option value="cancelled">❌ Cancelled / Failed</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="dlv-modal-label">Dispatch / Target Delivery Timestamp</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={deliveryDateInput}
                  onChange={(e) => setDeliveryDateInput(e.target.value)}
                  className="dlv-modal-input"
                />
              </Form.Group>

              {/* Remarks Field with Quick Presets */}
              <div className="dlv-remarks-field-group mb-3 p-3 rounded">
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-1">
                  <Form.Label className="dlv-modal-label mb-0">
                    Delivery Notes / Remarks {['cancelled', 'failed'].includes(newStatus) && <span className="text-danger">* (Required)</span>}
                  </Form.Label>
                  <span className="dlv-preset-hint">Click preset to fill:</span>
                </div>

                <div className="dlv-preset-chips mb-2 d-flex flex-wrap gap-1">
                  {DISPATCH_PRESETS.map((preset, pIdx) => (
                    <button
                      key={pIdx}
                      type="button"
                      className="dlv-preset-btn"
                      onClick={() => setRemarksInput(preset)}
                    >
                      {preset}
                    </button>
                  ))}
                </div>

                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter courier notes, customer instructions, or delivery confirmation details..."
                  value={remarksInput}
                  onChange={(e) => {
                    setRemarksInput(e.target.value);
                    if (formErrors.remarks) setFormErrors(prev => ({ ...prev, remarks: null }));
                  }}
                  className={`dlv-modal-textarea ${formErrors.remarks ? 'is-invalid' : ''}`}
                />
                {formErrors.remarks && (
                  <div className="text-danger small mt-1 fw-bold">
                    ⚠️ {formErrors.remarks}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-end gap-2 pt-3 border-top dlv-modal-footer-row">
                <Button variant="secondary" onClick={handleCloseModal} disabled={updatingStatus} className="dlv-modal-cancel-btn">
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={updatingStatus} className="dlv-modal-submit-btn fw-bold px-4">
                  {updatingStatus ? <><Spinner animation="border" size="sm" className="me-1" /> Updating...</> : 'Save Delivery Status'}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* Delivery Full Details Modal */}
      <Modal show={showDetailsModal} onHide={handleCloseDetailsModal} size="lg" scrollable className="dlv-modal-modern">
        <Modal.Header closeButton className="dlv-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="dlv-modal-icon">
              <FaMapMarkedAlt />
            </div>
            <div>
              <Modal.Title className="dlv-modal-title mb-0">Delivery Details & Manifest</Modal.Title>
              <div className="dlv-modal-subtitle">
                Order <span className="fw-bold text-primary">#{currentDelivery?.order_id}</span> • Delivery Log <span className="fw-bold text-info">#{currentDelivery?.delivery_log_id}</span>
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="dlv-modal-body p-4">
          {detailsLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="dlv-loading-text mt-2">Loading full manifest details...</p>
            </div>
          ) : (deliveryDetails || currentDelivery) ? (
            (() => {
              const data = deliveryDetails || currentDelivery;
              const fullAddress = [
                data.address_line1,
                data.address_line2,
                data.city,
                data.state,
                data.postal_code,
                data.country
              ].filter(Boolean).join(', ');

              return (
                <div className="dlv-manifest-container">
                  <Row className="g-3 mb-4">
                    <Col md={6}>
                      <div className="dlv-detail-card p-3 rounded">
                        <div className="dlv-detail-title mb-2">
                          <FaUser className="me-1 text-info" /> Courier Assignment
                        </div>
                        <div className="dlv-manifest-courier-name mb-1">{data.delivery_man_name || 'Unassigned'}</div>
                        <div className="dlv-manifest-subtext">Courier ID: <strong className="text-white">{data.delivery_person_id || 'N/A'}</strong></div>
                        {data.delivery_man_mobile && (
                          <div className="dlv-manifest-subtext mt-1">
                            <FaPhoneAlt className="me-1 text-info" /> Contact: <strong className="text-white">{data.delivery_man_mobile}</strong>
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="dlv-detail-card p-3 rounded">
                        <div className="dlv-detail-title mb-2">
                          <FaMapMarkerAlt className="me-1 text-danger" /> Delivery Destination
                        </div>
                        <div className="dlv-manifest-address mb-1">{fullAddress || 'Address on file'}</div>
                        {fullAddress && (
                          <a 
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="dlv-map-link small d-inline-flex align-items-center gap-1 mt-1"
                          >
                            <FaExternalLinkAlt /> Open in Google Maps
                          </a>
                        )}
                      </div>
                    </Col>
                  </Row>

                  <div className="dlv-status-bar p-3 rounded mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                      <span className="dlv-status-bar-label d-block mb-1">Current Transit Status:</span>
                      {getStatusBadge(data.status)}
                    </div>
                    <div className="text-sm-end">
                      <span className="dlv-status-bar-label d-block mb-1">Target / Delivered Date:</span>
                      <strong className="dlv-status-bar-date">{formatDate(data.delivery_date)}</strong>
                    </div>
                  </div>

                  {/* Items Manifest */}
                  <div className="dlv-items-table-wrapper rounded mb-3 overflow-hidden">
                    <div className="dlv-manifest-header p-2 px-3 fw-bold small border-bottom d-flex align-items-center gap-2">
                      <FaBoxOpen className="text-primary" /> Parcel Medicines Manifest ({data.order_items?.length || 0})
                    </div>
                    {Array.isArray(data.order_items) && data.order_items.length > 0 ? (
                      <Table responsive hover className="dlv-manifest-table mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Medicine</th>
                            <th>Category</th>
                            <th className="text-center">Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.order_items.map((it, i) => (
                            <tr key={i}>
                              <td className="dlv-item-name">{it.medicine_name}</td>
                              <td className="dlv-item-cat">{it.medicine_category || 'General'}</td>
                              <td className="text-center dlv-item-qty">x{it.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="p-3 text-center text-white small">No item details available.</div>
                    )}
                  </div>

                  {data.remarks && (
                    <div className="dlv-remarks-callout p-3 rounded">
                      <strong className="d-block mb-1">
                        <FaCommentAlt className="me-1 text-warning" /> Transit Remarks:
                      </strong>
                      <span className="dlv-remarks-text">{data.remarks}</span>
                    </div>
                  )}
                </div>
              );
            })()
          ) : null}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default DeliveryManagement;